import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, KeyRound } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => { if (!token) setError("This reset link is missing its token. Request a new one from the login page."); }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 2500);
    } catch (e2) {
      setError(formatApiError(e2));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex bg-white" data-testid="reset-password-page">
      <div className="hidden md:flex md:w-1/2 bg-black text-white p-12 flex-col justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="South Texas Strutters" className="w-14 h-14 object-contain bg-white rounded-lg p-1" />
          <div className="leading-tight">
            <div className="text-xl font-bold tracking-tight">STS Connect</div>
            <div className="text-xs text-slate-400 tracking-wide uppercase">South Texas Strutters</div>
          </div>
        </div>
        <div>
          <h1 className="text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
            Set a<br /><span className="text-[#D9232E]">new password.</span>
          </h1>
          <p className="mt-6 text-slate-300 text-base max-w-md leading-relaxed">
            Pick something you'll remember. Make it 6+ characters. You'll be signed in right after.
          </p>
        </div>
        <div className="text-xs text-slate-500 tracking-wider uppercase">Reset links expire in 1 hour</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="reset-form">
          <div className="md:hidden flex items-center gap-3 mb-8">
            <img src="/logo.png" alt="" className="w-10 h-10 object-contain" />
            <span className="text-xl font-bold">STS Connect</span>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-[#D9232E]" /> New password
            </h2>
            <p className="text-sm text-slate-500 mt-1">Set a new password for your account.</p>
          </div>

          {done ? (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3" data-testid="reset-success">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-emerald-900">Password updated!</div>
                <div className="text-xs text-emerald-700 mt-1">Redirecting you to sign in...</div>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="password">New password</Label>
                <Input id="password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="h-11" data-testid="reset-password-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm">Confirm new password</Label>
                <Input id="confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={6} className="h-11" data-testid="reset-confirm-input" />
              </div>
              {error && (
                <div className="text-sm text-[#D9232E] bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="reset-error">{error}</div>
              )}
              <Button type="submit" disabled={submitting || !token} className="w-full h-11 bg-[#D9232E] hover:bg-[#B91C25] text-white font-semibold" data-testid="reset-submit-button">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
              </Button>
              <p className="text-xs text-slate-400 text-center">
                <Link to="/login" className="text-[#D9232E] hover:underline">Back to sign in</Link>
              </p>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
